import { useState } from 'react';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import {
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import BackupIcon from '@mui/icons-material/Backup';
import SecurityIcon from '@mui/icons-material/Security';
import HistoryIcon from '@mui/icons-material/History';
import LanguageIcon from '@mui/icons-material/Language';
import StorageIcon from '@mui/icons-material/Storage';
import BugReportIcon from '@mui/icons-material/BugReport';
import InfoIcon from '@mui/icons-material/Info';
import PeopleIcon from '@mui/icons-material/People';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TimelineIcon from '@mui/icons-material/Timeline';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CodeIcon from '@mui/icons-material/Code';
import WebhookIcon from '@mui/icons-material/Webhook';
import ArticleIcon from '@mui/icons-material/Article';
import ComputerIcon from '@mui/icons-material/Computer';
import BuildIcon from '@mui/icons-material/Build';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DevicesIcon from '@mui/icons-material/Devices';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import TerminalIcon from '@mui/icons-material/Terminal';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ImportExportIcon from '@mui/icons-material/ImportExport';

export const DRAWER_WIDTH = 240;

interface NavLeaf {
  kind?: 'leaf';
  label: string;
  to: string;
  icon: React.ReactNode;
}

interface NavGroup {
  kind: 'group';
  label: string;
  icon: React.ReactNode;
  children: NavLeaf[];
}

interface NavDivider { kind: 'divider'; key: string }

type NavEntry = NavLeaf | NavGroup | NavDivider;

const nav: NavEntry[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <DashboardIcon /> },
  {
    kind: 'group',
    label: 'Server',
    icon: <StorageIcon />,
    children: [
      { label: 'Cron Jobs', to: '/cron',      icon: <ScheduleIcon /> },
      { label: 'MySQL',     to: '/mysql',     icon: <StorageIcon /> },
      { label: 'Services',  to: '/services',  icon: <SettingsApplicationsIcon /> },
      { label: 'Webserver', to: '/webserver', icon: <LanguageIcon /> },
    ],
  },
  {
    kind: 'group',
    label: 'Game Server',
    icon: <SportsEsportsIcon />,
    children: [
      { label: 'Backups',  to: '/backups',  icon: <BackupIcon /> },
      { label: 'Games',    to: '/games',    icon: <SportsEsportsIcon /> },
      { label: 'Wishlist', to: '/wishlist', icon: <BookmarkIcon /> },
    ],
  },
  {
    kind: 'group',
    label: 'User Management',
    icon: <PeopleIcon />,
    children: [
      { label: 'Add Users',           to: '/users',           icon: <PeopleIcon /> },
      { label: 'Login Activity',      to: '/login-activity',  icon: <TrackChangesIcon /> },
      { label: 'Manage Sessions',     to: '/sessions',        icon: <DevicesIcon /> },
    ],
  },
  { kind: 'divider', key: 'div-admin' },
  {
    kind: 'group',
    label: 'Implementing',
    icon: <BuildIcon />,
    children: [
      { label: 'Claude',      to: '/claude',      icon: <SmartToyIcon /> },
      { label: 'Debugging',   to: '/debugging',   icon: <TerminalIcon /> },
      { label: 'Development', to: '/development', icon: <CodeIcon /> },
    ],
  },
  {
    kind: 'group',
    label: 'Notifications',
    icon: <NotificationsIcon />,
    children: [
      { label: 'Discord',          to: '/discord',         icon: <WebhookIcon /> },
      { label: 'Resource Alerts',  to: '/resource-alert',  icon: <NotificationsActiveIcon /> },
    ],
  },
  {
    kind: 'group',
    label: 'Logs',
    icon: <ArticleIcon />,
    children: [
      { label: 'Audit', to: '/audit',  icon: <HistoryIcon /> },
      { label: 'Error', to: '/errors', icon: <BugReportIcon /> },
    ],
  },
  {
    kind: 'group',
    label: 'System',
    icon: <ComputerIcon />,
    children: [
      { label: 'Firewall',      to: '/firewall',        icon: <SecurityIcon /> },
      { label: 'IP Bans',       to: '/security',        icon: <GppMaybeIcon /> },
      { label: 'Security Audit',to: '/security-audit',  icon: <VerifiedUserIcon /> },


    ],
  },
    {
    kind: 'group',
    label: 'Settings',
    icon: <BuildIcon />,
    children: [
      { label: 'Export',      to: '/settings',        icon: <ImportExportIcon /> },
    ],
  },
  {
    kind: 'group',
    label: 'Help',
    icon: <HelpOutlineIcon />,
    children: [
      { label: 'Auth Flow', to: '/help/flow',   icon: <TimelineIcon /> },
    ],
  },
  { label: 'About', to: '/about', icon: <InfoIcon /> },
];

function NavLeafItem({ item, indent = false }: { item: NavLeaf; indent?: boolean }) {
  const location = useLocation();
  const selected = location.pathname === item.to;
  return (
    <ListItem disablePadding>
      <ListItemButton
        component={RouterNavLink}
        to={item.to}
        selected={selected}
        sx={indent ? { pl: 4 } : undefined}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
        <ListItemText primary={item.label} />
      </ListItemButton>
    </ListItem>
  );
}

function NavGroupItem({ group }: { group: NavGroup }) {
  const location = useLocation();
  const anyChildActive = group.children.some((c) => location.pathname === c.to);
  const [open, setOpen] = useState(anyChildActive);

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton onClick={() => setOpen((o) => !o)} selected={anyChildActive && !open}>
          <ListItemIcon sx={{ minWidth: 40 }}>{group.icon}</ListItemIcon>
          <ListItemText primary={group.label} />
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </ListItemButton>
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List disablePadding>
          {group.children.map((child) => (
            <NavLeafItem key={child.to} item={child} indent />
          ))}
        </List>
      </Collapse>
    </>
  );
}

export function Sidebar() {
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
      }}
    >
      <Toolbar sx={{ px: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, lineHeight: 1 }}>
            WebAdmin
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            tsfx.dustinity.de
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ py: 1 }}>
        {nav.map((entry) =>
          entry.kind === 'group' ? (
            <NavGroupItem key={entry.label} group={entry} />
          ) : entry.kind === 'divider' ? (
            <Divider key={entry.key} sx={{ my: 1 }} />
          ) : (
            <NavLeafItem key={entry.to} item={entry} />
          ),
        )}
      </List>
    </Drawer>
  );
}
